import React, { useState, useEffect } from 'react';
import { Phone, PhoneOff, Video } from 'lucide-react';

export const IncomingCallNotification = ({ 
  caller, 
  roomId, 
  onAccept, 
  onDecline,
  timeout = 30000 
}) => {
  const [timeLeft, setTimeLeft] = useState(timeout / 1000);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Countdown timer
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleDecline(); // Auto-decline on timeout
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleAccept = () => {
    setIsVisible(false);
    onAccept(roomId);
  };

  const handleDecline = () => {
    setIsVisible(false);
    onDecline(roomId);
  };

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '30%',
      left: '40%',
      transform: 'translate(-50%, -50%)',
      zIndex: 2000,
      minWidth: '320px',
      maxWidth: '400px',
      background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
      borderRadius: '20px',
      padding: '24px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)',
      border: '2px solid rgba(16, 185, 129, 0.5)',
      animation: 'slideIn 0.3s ease, pulse 2s infinite'
    }}>
      {/* Caller Info */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '20px'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #10b981, #059669)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '28px',
          fontWeight: '700',
          color: 'white',
          textTransform: 'uppercase',
          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
          animation: 'ping 1s infinite'
        }}>
          {caller?.first_name?.charAt(0) || 'U'}
        </div>

        <div style={{ flex: 1 }}>
          <h3 style={{
            margin: '0 0 4px 0',
            fontSize: '20px',
            fontWeight: '700',
            color: 'white'
          }}>
            {caller?.first_name} {caller?.last_name}
          </h3>
          <p style={{
            margin: 0,
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <Video size={14} />
            Incoming video call
          </p>
        </div>
      </div>

      {/* Timer */}
      <div style={{
        marginBottom: '20px',
        textAlign: 'center'
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          fontSize: '14px',
          color: timeLeft <= 5 ? '#fbbf24' : 'rgba(255, 255, 255, 0.8)'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: timeLeft <= 5 ? '#fbbf24' : '#10b981',
            animation: 'pulse 1s infinite'
          }} />
          {timeLeft}s remaining
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '12px'
      }}>
        <button
          onClick={handleDecline}
          style={{
            flex: 1,
            padding: '14px',
            background: '#ef4444',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            color: 'white',
            fontSize: '16px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#dc2626'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#ef4444'}
        >
          <PhoneOff size={20} />
          Decline
        </button>

        <button
          onClick={handleAccept}
          style={{
            flex: 1,
            padding: '14px',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            color: 'white',
            fontSize: '16px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
          }}
        >
          <Phone size={20} />
          Accept
        </button>
      </div>

      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }

        @keyframes ping {
          0% {
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
          }
          50% {
            box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
          }
        }
      `}</style>
    </div>
  );
};
